import { Permission, MATTER_SCOPED_PERMISSIONS } from '../../../backend/src/auth/permissions/permission';
import { REGISTRY } from '../../../backend/src/shared/action-registry';

export { PERMISSION_DESCRIPTIONS } from '../../../backend/src/auth/permissions/permission-groups';

export interface PermissionInfo {
  permission: Permission;
  description: string;
  delegatedDescriptions?: string[];
  /** If set, this permission is controlled by these parent permission(s) and should not be directly editable. */
  delegatedBy?: string[];
  /** True when this permission lives under a matter resource but is org-scoped (not overridable at the matter level). */
  isOrgScoped?: boolean;
  scope: 'organisation' | 'platform';
}

export interface PermissionGroup {
  name: string;
  depth: number;
  permissions: PermissionInfo[];
  scope: 'organisation' | 'platform';
}

/** Set of permissions that are delegated by a parent — hidden from the roles matrix. */
const delegatedChildren = new Set(Object.keys(REGISTRY.delegatedBy));

/** Inverted map: parent permission → child permissions it delegates */
const parentToChildren: Record<string, string[]> = {};
for (const [child, parents] of Object.entries(REGISTRY.delegatedBy)) {
  for (const parent of parents) {
    if (!parentToChildren[parent]) parentToChildren[parent] = [];
    parentToChildren[parent].push(child);
  }
}

/** Check whether a resource is 'matter' or a descendant of 'matter' in the hierarchy. */
function isMatterDescendant(resource: string): boolean {
  let current: string | null = resource;
  while (current !== null) {
    if (current === 'matter') return true;
    current = REGISTRY.hierarchy[current] ?? null;
  }
  return false;
}

/** Groups derived from the `resource:action` permission strings, with display names. */
export const PERMISSION_GROUPS: PermissionGroup[] = (() => {
  const groups = new Map<string, PermissionInfo[]>();

  for (const [permission, description] of Object.entries(REGISTRY.descriptions)) {
    const resource = permission.split(':')[0];
    if (!groups.has(resource)) groups.set(resource, []);

    const isDelegated = delegatedChildren.has(permission);
    const children = !isDelegated ? (parentToChildren[permission] as string[] | undefined) : undefined;
    const delegatedDescriptions =
      children && children.length > 0 ? children.map((c) => REGISTRY.descriptions[c]).filter(Boolean) : undefined;
    const delegatedByParents = isDelegated ? (REGISTRY.delegatedBy[permission] as string[]) : undefined;

    // Flag permissions that live under a matter resource but cannot be overridden at the matter level
    const isOrgScoped =
      isMatterDescendant(resource) && !MATTER_SCOPED_PERMISSIONS.has(permission as Permission) ? true : undefined;

    const info: PermissionInfo = {
      permission: permission as Permission,
      description,
      scope: REGISTRY.permissionScopes[permission] ?? 'organisation',
      ...(delegatedDescriptions && delegatedDescriptions.length > 0 ? { delegatedDescriptions } : {}),
      ...(delegatedByParents && delegatedByParents.length > 0 ? { delegatedBy: delegatedByParents } : {}),
      ...(isOrgScoped ? { isOrgScoped } : {}),
    };
    groups.get(resource)!.push(info);
  }

  // Compute depth of each resource from the hierarchy
  const getDepth = (resource: string): number => {
    const parent = REGISTRY.hierarchy[resource];
    if (!parent) return 0;
    return 1 + getDepth(parent);
  };

  // Build tree-ordered list: parent before children, depth-first
  const resourceKeys = Array.from(groups.keys());
  const ordered: string[] = [];
  const addWithChildren = (resource: string) => {
    ordered.push(resource);
    for (const r of resourceKeys) {
      if (REGISTRY.hierarchy[r] === resource && !ordered.includes(r)) {
        addWithChildren(r);
      }
    }
  };
  // Start from roots (resources with no parent or whose parent has no permissions)
  for (const r of resourceKeys) {
    const parent = REGISTRY.hierarchy[r];
    if (!parent || !groups.has(parent)) {
      if (!ordered.includes(r)) addWithChildren(r);
    }
  }
  // Add any remaining (shouldn't happen but safety)
  for (const r of resourceKeys) {
    if (!ordered.includes(r)) ordered.push(r);
  }

  return ordered
    .map((resource) => ({
      name: REGISTRY.displayNames[resource] ?? resource,
      depth: getDepth(resource),
      permissions: groups.get(resource)!,
      scope: groups.get(resource)!.some((permission) => permission.scope === 'platform')
        ? ('platform' as const)
        : ('organisation' as const),
    }))
    .sort((a, b) => Number(b.scope === 'platform') - Number(a.scope === 'platform'));
})();
